import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/toast';
import { IconX as X } from '@tabler/icons-react';
import { updateAdminUser, type AdminUser } from '@/api/adminApi';
import { useAuth } from '@/contexts/auth-context';
import { btnAccent, btnGhost, input, label } from '@/components/ui/design';

export default function UserEditor({ user, onClose }: { user: AdminUser; onClose: () => void }) {
    const queryClient = useQueryClient();
    const { user: me } = useAuth();
    const isSelf = Boolean(me?.email && user.email && me.email.toLowerCase() === user.email.toLowerCase());
    const [firstName, setFirstName] = useState(user.firstName ?? '');
    const [lastName, setLastName] = useState(user.lastName ?? '');
    const [phone, setPhone] = useState(user.phone ?? '');
    const [isAdmin, setIsAdmin] = useState(user.isAdmin);

    const save = useMutation({
        mutationFn: () => updateAdminUser(user._id, { firstName, lastName, phone, isAdmin }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            addToast({ title: 'Saved', description: 'User updated.', color: 'success' });
            onClose();
        },
        onError: (error: any) => {
            addToast({ title: 'Could not save', description: error?.response?.data?.message || error?.message || 'Please try again.', color: 'danger' });
        },
    });

    const submit = () => {
        if (isAdmin !== user.isAdmin && isAdmin && !window.confirm(`Make ${user.email} an admin? Admins can manage every organization and user.`)) return;
        save.mutate();
    };

    return (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/50 p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div role="dialog" aria-modal="true" aria-label="Edit user" className="w-full max-w-lg rounded-[28px] bg-white p-6 shadow-2xl dark:bg-nightSurface">
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Edit user</h2>
                        <p className="truncate text-sm text-grey40 dark:text-nightMuted">{user.email}</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/10"><X className="h-4 w-4" /></button>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label><span className={label}>First name</span><input value={firstName} onChange={e => setFirstName(e.target.value)} className={input} /></label>
                    <label><span className={label}>Last name</span><input value={lastName} onChange={e => setLastName(e.target.value)} className={input} /></label>
                    <label className="sm:col-span-2"><span className={label}>Phone</span><input value={phone} onChange={e => setPhone(e.target.value)} className={input} /></label>
                </div>
                <label className="mt-5 flex items-start gap-3 text-sm font-semibold text-grey10 dark:text-white">
                    <input type="checkbox" checked={isAdmin} disabled={isSelf && user.isAdmin} onChange={e => setIsAdmin(e.target.checked)} className="mt-0.5 h-4 w-4" />
                    <span>
                        Site admin
                        <span className="block font-normal text-grey40 dark:text-nightMuted">{isSelf && user.isAdmin ? 'You cannot remove your own admin permission.' : 'Can open the admin panel and manage all organizations and users.'}</span>
                    </span>
                </label>
                <div className="mt-6 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                    <button type="button" onClick={submit} disabled={save.isPending} className={btnAccent}>{save.isPending ? 'Saving...' : 'Save changes'}</button>
                </div>
            </div>
        </div>
    );
}
